import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  api,
  type AdminDevice,
  type Dataset,
  type DatasetDetail,
  type LiveCommand,
  type LiveDevice,
  type LiveEvent,
  type LiveSample,
  type LiveSession,
  type LiveSnapshot,
  type LiveStatus,
  type PublicDemo,
  type PublicDevice,
  type Sample,
} from './api';

export function usePublicDemo() {
  return useQuery<PublicDemo>({
    queryKey: ['public-demo'],
    queryFn: () => api<PublicDemo>('/api/public/demo'),
    retry: false,
    staleTime: 60_000,
  });
}

export function usePublicDevices() {
  return useQuery<PublicDevice[]>({
    queryKey: ['public-devices'],
    queryFn: async () => (await api<{ devices: PublicDevice[] }>('/api/public/devices')).devices,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useDatasets() {
  return useQuery<Dataset[]>({
    queryKey: ['datasets'],
    queryFn: async () => (await api<{ datasets: Dataset[] }>('/api/ccs/datasets')).datasets,
    staleTime: 30_000,
  });
}

export function useDataset(id: string | undefined) {
  return useQuery<DatasetDetail>({
    queryKey: ['dataset', id],
    queryFn: () => api<DatasetDetail>(`/api/ccs/datasets/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useSamples(id: string | undefined, center: string | null) {
  return useQuery<Sample[]>({
    queryKey: ['samples', id, center],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '300' });
      if (center) params.set('center', center);
      const result = await api<{ samples: Sample[] }>(
        `/api/ccs/datasets/${encodeURIComponent(id!)}/samples?${params.toString()}`,
      );
      return result.samples;
    },
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

const CURVE_WINDOW_MS = 5 * 60_000;
const CURVE_LOOKAHEAD_MS = 3 * 60_000;
const CURVE_LOAD_MARGIN_MS = 60_000;
const CURVE_REFETCH_AHEAD_MS = 90_000;
const CURVE_REFETCH_BEHIND_MS = 60_000;

export type SeriesPoint = { ts: string; value: number };

type SeriesResponse = {
  channel: string;
  from: string;
  to: string;
  points: SeriesPoint[];
};

/**
 * 曲线的数据窗口与播放游标解耦：
 * - 以 anchor 为锚点，一次取回 [anchor-6min, anchor+3min]；
 * - 播放推进到预取余量不足时，把锚点前移到当前游标并重新取数；
 * - 观众端（演示房间）同样由同步的 cursor 驱动，表现与主持人一致。
 */
export function useChannelSeries(
  datasetId: string | undefined,
  channelKey: string | null,
  cursor: string | null,
) {
  const cursorMs = cursor ? new Date(cursor).getTime() : null;
  const [anchor, setAnchor] = useState<number | null>(() =>
    cursorMs !== null && Number.isFinite(cursorMs) ? cursorMs : null,
  );

  useEffect(() => {
    if (cursorMs === null || Number.isNaN(cursorMs)) {
      setAnchor(null);
      return;
    }
    setAnchor((prev) => {
      if (prev === null) return cursorMs;
      // 后退超过 1 分钟（拖动/跳转）时立即换窗
      if (cursorMs < prev - CURVE_REFETCH_BEHIND_MS) return cursorMs;
      // 剩余预取不足时向前滚动
      if (cursorMs > prev + CURVE_LOOKAHEAD_MS - CURVE_REFETCH_AHEAD_MS) return cursorMs;
      return prev;
    });
  }, [cursorMs]);

  const from =
    anchor === null ? null : new Date(anchor - CURVE_WINDOW_MS - CURVE_LOAD_MARGIN_MS).toISOString();
  const to = anchor === null ? null : new Date(anchor + CURVE_LOOKAHEAD_MS).toISOString();

  const query = useQuery<SeriesResponse>({
    queryKey: ['series', datasetId, channelKey, from, to],
    queryFn: () => {
      const params = new URLSearchParams({
        channel: channelKey!,
        from: from!,
        to: to!,
        maxPoints: '600',
      });
      return api<SeriesResponse>(
        `/api/ccs/datasets/${encodeURIComponent(datasetId!)}/series?${params.toString()}`,
      );
    },
    enabled: Boolean(datasetId && channelKey && from && to),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  // 切换通道时 keepPreviousData 会短暂保留旧通道的数据，必须校验后再用
  const ready = Boolean(query.data && query.data.channel === channelKey);

  return {
    points: ready ? query.data!.points : [],
    loading: Boolean(channelKey && anchor !== null) && (query.isFetching || !ready),
    failed: Boolean(channelKey && anchor !== null) && query.isError,
  };
}

export type LiveConnection = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type LiveState = {
  connection: LiveConnection;
  device: LiveDevice | null;
  session: LiveSession | null;
  sample: LiveSample | null;
  samples: LiveSample[];
  status: LiveStatus | null;
  events: LiveEvent[];
  commands: LiveCommand[];
  canControl: boolean;
  serverTime: string | null;
};

const EMPTY_LIVE: LiveState = {
  connection: 'connecting',
  device: null,
  session: null,
  sample: null,
  samples: [],
  status: null,
  events: [],
  commands: [],
  canControl: false,
  serverTime: null,
};

export function useLiveDevices() {
  return useQuery<LiveDevice[]>({
    queryKey: ['live-devices'],
    queryFn: async () => (await api<{ devices: LiveDevice[] }>('/api/ccs/live/devices')).devices,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

/**
 * 实时数据源：先用 HTTP 拿一次快照，再连 /ws/live/:id 收增量。
 * 断线后 1s→2s→4s→8s→10s 退避重连；重连成功由服务端再发 snapshot 对齐。
 */
export function useLive(deviceId: string | undefined): LiveState {
  const [state, setState] = useState<LiveState>(EMPTY_LIVE);

  useEffect(() => {
    if (!deviceId) return undefined;
    let closed = false;
    let socket: WebSocket | null = null;
    let timer: number | null = null;
    let attempt = 0;

    setState({ ...EMPTY_LIVE, connection: 'connecting' });

    const applySnapshot = (snapshot: LiveSnapshot) => {
      setState((prev) => {
        const prevLatest = prev.sample?.seq ?? -1;
        const nextLatest = snapshot.latest?.seq ?? -1;
        if (prevLatest >= 0 && nextLatest < prevLatest) return prev;
        return {
          connection: 'open',
          device: snapshot.device,
          session: snapshot.session,
          sample: snapshot.latest ?? snapshot.samples[snapshot.samples.length - 1] ?? null,
          samples: snapshot.samples.slice(-300),
          status: snapshot.status,
          events: snapshot.events,
          commands: snapshot.commands ?? [],
          canControl: snapshot.canControl,
          serverTime: snapshot.serverTime,
        };
      });
    };

    const applySample = (sample: LiveSample) => {
      setState((prev) => {
        if (prev.sample && sample.seq <= prev.sample.seq) return prev;
        const samples = [...prev.samples, sample];
        return {
          ...prev,
          sample,
          samples: samples.length > 300 ? samples.slice(samples.length - 300) : samples,
        };
      });
    };

    const handle = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const message = raw as { type?: string };
      if (message.type === 'snapshot') {
        applySnapshot(raw as LiveSnapshot);
      } else if (message.type === 'sample') {
        const sample = (raw as { sample?: LiveSample }).sample;
        if (sample) applySample(sample);
      } else if (message.type === 'status') {
        const payload = raw as {
          device?: LiveDevice;
          session?: LiveSession | null;
          status?: LiveStatus | null;
        };
        setState((prev) => ({
          ...prev,
          device: payload.device ?? prev.device,
          session: payload.session === undefined ? prev.session : payload.session,
          status: payload.status === undefined ? prev.status : payload.status,
        }));
      } else if (message.type === 'event') {
        const event = (raw as { event?: LiveEvent }).event;
        if (event) {
          setState((prev) => ({
            ...prev,
            events: [event, ...prev.events].slice(0, 100),
          }));
        }
      } else if (message.type === 'command') {
        const command = (raw as { command?: LiveCommand }).command;
        if (command) {
          setState((prev) => {
            const index = prev.commands.findIndex((item) => item.id === command.id);
            const commands = [...prev.commands];
            if (index >= 0) commands[index] = command;
            else commands.unshift(command);
            return { ...prev, commands: commands.slice(0, 20) };
          });
        }
      }
    };

    const connect = () => {
      if (closed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${protocol}://${window.location.host}/ws/live/${encodeURIComponent(deviceId)}`;
      socket = new WebSocket(url);

      socket.onopen = () => {
        attempt = 0;
        setState((prev) => ({ ...prev, connection: 'open' }));
      };

      socket.onmessage = (event) => {
        try {
          handle(JSON.parse(event.data as string));
        } catch {
          // ignore malformed frame
        }
      };

      socket.onclose = () => {
        if (closed) return;
        setState((prev) => ({ ...prev, connection: 'reconnecting' }));
        const delay = Math.min(10_000, 1_000 * 2 ** attempt);
        attempt += 1;
        timer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // onclose 会负责重连
      };
    };

    api<LiveSnapshot>(`/api/ccs/live/devices/${encodeURIComponent(deviceId)}`)
      .then((snapshot) => {
        if (!closed) applySnapshot(snapshot);
      })
      .catch(() => {
        // 设备不存在时让 WS 去报错；页面会显示空态
      });

    connect();

    return () => {
      closed = true;
      if (timer !== null) window.clearTimeout(timer);
      socket?.close();
    };
  }, [deviceId]);

  return state;
}

export function useAdminDevices(enabled = true) {
  return useQuery<AdminDevice[]>({
    queryKey: ['admin-devices'],
    queryFn: async () => (await api<{ devices: AdminDevice[] }>('/api/admin/devices')).devices,
    enabled,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}